// Members (admin only): everyone who signed in, access requests first. Approve a pending member to
// open the live board for them, or invite an email to pre-approve it.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useSearchParams } from '@/lib/nav';
import { useIndexStrip, useMarket } from '@/lib/client/use-market';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AppHeader, Page } from './app-header';
import { carry, dateSeed, indexById } from './common';
import { ValidationPanel } from './validation-panel';

interface Member {
  email: string; name: string; displayName: string; role: 'admin' | 'member'; status: 'invited' | 'pending' | 'active' | 'disabled';
  invitedBy: string; createdAt: string; lastLoginAt: string | null; accessRequestedAt: string | null;
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const STATUS_STYLE: Record<Member['status'], string> = {
  active: 'bg-bull-soft text-bull-strong', invited: 'bg-brand-soft text-brand-strong', pending: 'bg-warn-soft text-warn', disabled: 'bg-muted text-muted-foreground',
};

export function AdminApp() {
  const params = useSearchParams();
  const seed = Number(params.get('seed')) || dateSeed();
  const index = indexById(params.get('index'))?.id ?? 'FNO';
  const { session, status, mode, live, version, user, role } = useMarket({ index: 'NIFTY50', seed });
  const strip = useIndexStrip(session, live, version);
  const query = carry({ index, live, seed });
  const [members, setMembers] = useState<Member[] | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api('/v1/admin/users');
    if (r.ok) setMembers(await r.json());
    else setMembers([]);
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { document.title = 'Members · MoneyHQ'; }, []);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await api('/v1/admin/users', { method: 'POST', body: JSON.stringify({ email }) });
    setBusy(false);
    if (!r.ok) return toast.error((await r.json().catch(() => ({}))).error ?? 'Could not invite');
    toast.success(`${email} is approved: the live board opens when they sign in with Google`);
    setEmail('');
    void load();
  }

  async function setStatus(m: Member, next: Member['status']) {
    const r = await api(`/v1/admin/users/${encodeURIComponent(m.email)}`, { method: 'PATCH', body: JSON.stringify({ status: next }) });
    if (!r.ok) return toast.error('Could not update');
    void load();
  }

  return (
    <>
      <AppHeader section="admin" mode={mode} status={status} user={user} role={role} query={query} strip={strip} />
      <Page>
        <h1 className="text-[26px] font-semibold">Members</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">Anyone can sign in with Google and practise on simulated data. Approve people to open the live board, or invite an email to approve it in advance.</p>
        {role === 'member' && <p className="mt-6 text-sm text-bear">Only the admin can manage members.</p>}
        {role === 'admin' && (
          <>
            <form onSubmit={invite} className="mt-6 flex max-w-xl gap-2">
              <Input type="email" required placeholder="trader@gmail.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 bg-card" aria-label="Email to approve in advance" />
              <Button type="submit" disabled={busy} className="h-10 rounded-full px-5">Invite</Button>
            </form>
            <Card className="mt-6 gap-0 py-0">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Member</TableHead><TableHead>Status</TableHead><TableHead className="hidden md:table-cell">Joined</TableHead>
                  <TableHead className="hidden md:table-cell">Last sign-in</TableHead><TableHead className="pr-5 text-right">Access</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(members ?? []).map((m) => (
                    <TableRow key={m.email}>
                      <TableCell className="py-3 pl-5">
                        <div className="font-medium text-ink">{m.displayName || m.name || m.email}</div>
                        <div className="text-xs text-muted-foreground">{m.displayName || m.name ? m.email : ''}{m.role === 'admin' ? (m.name ? ' · admin' : 'admin') : ''}</div>
                        {m.status === 'pending' && m.accessRequestedAt && <div className="mt-0.5 text-xs font-medium text-warn">Asked for access {when(m.accessRequestedAt)}</div>}
                      </TableCell>
                      <TableCell><span className={cn('inline-flex h-6 items-center rounded-full px-2.5 text-xs font-medium capitalize', STATUS_STYLE[m.status])}>{m.status}</span></TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{when(m.createdAt)}{m.invitedBy ? ` by ${m.invitedBy}` : ''}</TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{when(m.lastLoginAt)}</TableCell>
                      <TableCell className="pr-5 text-right">
                        {m.role === 'admin' ? <span className="text-xs text-muted-foreground">—</span>
                          : m.status === 'disabled'
                            ? <Button variant="outline" size="sm" className="rounded-full" onClick={() => setStatus(m, 'pending')}>Restore</Button>
                            : m.status === 'pending'
                              ? (
                                <span className="inline-flex gap-1.5">
                                  <Button size="sm" className="rounded-full" onClick={() => setStatus(m, 'active')}>Approve</Button>
                                  <Button variant="outline" size="sm" className="rounded-full text-bear hover:text-bear" onClick={() => setStatus(m, 'disabled')}>Block</Button>
                                </span>
                              )
                              : <Button variant="outline" size="sm" className="rounded-full text-bear hover:text-bear" onClick={() => setStatus(m, m.status === 'active' ? 'pending' : 'disabled')}>
                                  {m.status === 'active' ? 'Revoke access' : 'Remove'}
                                </Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                  {members?.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No members yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
            <ValidationPanel />
          </>
        )}
      </Page>
    </>
  );
}
